/**
 * Форма «Что сеем» на выбранный участок (всё поле ИЛИ зона) — эталон founder v3.
 *
 * Три плитки: «Посеять культуру» / «Оставить под пар» / «Пока пусто». Культура —
 * справочник + план ц/га + прогресс (план→посеян→вегетация→убран). Пар — подтип
 * (чёрный/сидеральный/занятый/полупар) + опц. сидерат. «Повторить прошлый сезон»
 * подставляет культуру предыдущего сезона того же участка.
 *
 * Полям рисовой системы (fields.is_rice_system) в том же списке предлагается
 * АМП — агромелиоративное поле. На обычных полях его нет: АМП бывает только на
 * рису (backend валидирует то же самое — 422).
 *
 * Save → POST /occupancy (создать) или PATCH /occupancy/{id} (правка). XOR-конфликт
 * (поле целиком ⊕ зоны) прилетает 409 с человеческим текстом от backend → toast.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Sprout, Undo2, Wheat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import type { Crop } from '@/lib/map/useCropsReference';
import {
  useCreateOccupancy,
  useCropGoalIds,
  useDeleteOccupancy,
  useGrowingGoals,
  usePatchOccupancy,
} from './api';
import { OccupancyProgress } from './OccupancyProgress';
import { fieldStateOptions, SIDERAT_FALLOW_KINDS } from './constants';
import { useFieldStates } from '@/lib/reference/fieldCatalogs';
import {
  cropPickerKey,
  useAdoptCanonCrop,
  useCropPicker,
  type CropPickerItem,
} from './api';
import {
  allowedGoalOptions,
  pruneSelectedGoals,
  withSavedGoals,
} from './growingGoals';
import { sowButtonLabel } from './rotationLogic';
import type { CropHistoryRow, OccupancyLeaf } from './types';

type SowKind = 'crop' | 'fallow' | 'none';

interface Props {
  fieldId: string;
  seasonId: string;
  leaf: OccupancyLeaf;
  crops: Crop[];
  canWrite: boolean;
  /** Прошлогодний посев этого участка — для «↩︎ Повторить прошлый сезон». */
  repeatCandidate?: CropHistoryRow | null;
  /** Поле рисовой системы → в списке состояний появляется АМП. */
  isRiceSystem?: boolean;
  /**
   * Почему сейчас нельзя сохранить посев на этот участок (поле уже засеяно
   * целиком / уже засеяны зоны). Сохранение блокируем, «Снять посев»
   * оставляем — это и есть выход из ситуации (AK-449).
   */
  blockedReason?: string | null;
}

const TILES: ReadonlyArray<{
  kind: SowKind;
  icon: typeof Wheat;
  title: string;
  hint: string;
}> = [
  { kind: 'crop', icon: Wheat, title: 'Посеять культуру', hint: 'посев и уборка' },
  { kind: 'fallow', icon: Sprout, title: 'Оставить под пар', hint: 'поле отдыхает' },
  { kind: 'none', icon: Undo2, title: 'Пока пусто', hint: 'не запланировано' },
];

export function SowForm({
  fieldId,
  seasonId,
  leaf,
  crops,
  canWrite,
  repeatCandidate,
  isRiceSystem = false,
  blockedReason = null,
}: Props) {
  const s = leaf.sowing;
  const initialKind: SowKind = s?.occupancy_type ?? 'crop';
  const [kind, setKind] = useState<SowKind>(initialKind);
  // Выбор культуры — по НОМЕНКЛАТУРЕ хозяйства (SA-04). Ключ строки:
  // id номенклатуры, а для культуры, до номенклатуры не доехавшей, —
  // 'crop:<canon-id>'. Канон-id держим отдельно: от него зависят цели.
  const picker = useCropPicker();
  const adopt = useAdoptCanonCrop();
  const [cropKey, setCropKey] = useState<string>(
    s?.occupancy_type === 'crop'
      ? (s?.local_catalog_item_id ?? (s?.crop_id ? `crop:${s.crop_id}` : ''))
      : '',
  );
  const [yieldStr, setYieldStr] = useState<string>(
    s?.planned_yield_c_ha != null ? String(s.planned_yield_c_ha) : '',
  );
  const [fallowKind, setFallowKind] = useState<string>(
    s?.fallow_kind ?? 'fallow_black',
  );
  const [sideratId, setSideratId] = useState<string>(
    s?.occupancy_type === 'fallow' ? (s?.crop_id ?? '') : '',
  );
  // «Назначение посева» — необязательный набор целей выращивания.
  const [goalIds, setGoalIds] = useState<string[]>(
    s?.occupancy_type === 'crop' ? (s.growing_goals ?? []).map((g) => g.id) : [],
  );

  const pickerItems = useMemo(() => picker.data ?? [], [picker.data]);
  const selectedCrop: CropPickerItem | null = useMemo(
    () => pickerItems.find((i) => cropPickerKey(i) === cropKey) ?? null,
    [pickerItems, cropKey],
  );
  const cropId = selectedCrop?.crop_id ?? '';
  // Культуры общего справочника, которых у хозяйства ещё нет — для «добавить».
  const addableCrops = useMemo(() => {
    const known = new Set(pickerItems.map((i) => i.crop_id).filter(Boolean));
    return crops.filter((c) => !known.has(c.id));
  }, [crops, pickerItems]);

  // Состояния поля — из reference.field_states, а не из константы в коде.
  // Набор опций прежний: виды пара (`fallow_*`), а полям рисовой системы —
  // плюс АМП (см. fieldStateOptions); выводное поле / под мелиорацией
  // задаются не в форме посева.
  const fieldStates = useFieldStates();

  const goalsRef = useGrowingGoals();
  // Допустимые цели тянем только когда культура реально выбрана.
  const cropGoalIds = useCropGoalIds(kind === 'crop' && cropId ? cropId : null);
  // Уже сохранённые назначения этого посева — пока культуру не меняли, они
  // остаются в списке, даже если справочник культуры с тех пор ужали.
  const savedGoals =
    s?.occupancy_type === 'crop' && cropId === s.crop_id
      ? (s.growing_goals ?? [])
      : [];
  const goalOptions = useMemo(
    () =>
      withSavedGoals(
        allowedGoalOptions(goalsRef.data ?? [], cropGoalIds.data),
        savedGoals,
      ),
    // savedGoals пересобирается на каждом рендере — сравниваем по составу.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goalsRef.data, cropGoalIds.data, savedGoals.map((g) => g.id).join(',')],
  );
  // Сменили культуру → снимаем назначения, которых у неё нет.
  useEffect(() => {
    if (goalOptions.length === 0) return;
    setGoalIds((prev) => pruneSelectedGoals(prev, goalOptions));
  }, [goalOptions]);

  const create = useCreateOccupancy(fieldId);
  const patch = usePatchOccupancy(fieldId);
  const remove = useDeleteOccupancy(fieldId);
  const saving = create.isPending || patch.isPending || remove.isPending;
  // «Снять посев» — отдельное право: роут архивации требует sowings:D, а
  // canWrite карточки идёт от update. Без этой проверки у агронома с правом
  // «править, но не удалять» кнопка была бы видна и упиралась в 403.
  const canRemove = useCan(PERM.cropWrite, 'd');

  const showSiderat = kind === 'fallow' && SIDERAT_FALLOW_KINDS.has(fallowKind);
  // Виды пара + (только для рисовой системы) АМП. Уже сохранённое состояние
  // остаётся в списке, даже если признак поля с тех пор сняли — иначе агроном
  // видел бы пустой выбор и не понимал, что стоит на поле.
  const stateOptions = useMemo(() => {
    const items = fieldStates.data ?? [];
    const base = fieldStateOptions(items, isRiceSystem);
    if (fallowKind && !base.some((o) => o.code === fallowKind)) {
      // Подпись сохранённого состояния берём из справочника. Если строки там
      // нет (справочник не приехал или её выключили) — опцию не выдумываем:
      // код состояния на экран не течёт, ниже покажется честное «справочник
      // недоступен».
      const saved = items.find((i) => i.code === fallowKind);
      if (saved) return [...base, saved];
    }
    return base;
  }, [fieldStates.data, isRiceSystem, fallowKind]);

  function handleError(err: unknown) {
    if (err instanceof ApiError) toast.error(err.detail);
    else toast.error('Не удалось сохранить посев');
  }

  async function handleRemove() {
    if (!s) return;
    try {
      await remove.mutateAsync(s.sowing_id);
      toast.success(`Посев снят с участка «${leaf.name}»`);
    } catch (err) {
      handleError(err);
    }
  }

  function repeatLastSeason() {
    if (!repeatCandidate) return;
    setKind('crop');
    const again = repeatCandidate.crop_id
      ? pickerItems.find((i) => i.crop_id === repeatCandidate.crop_id)
      : undefined;
    setCropKey(
      again
        ? cropPickerKey(again)
        : repeatCandidate.crop_id
          ? `crop:${repeatCandidate.crop_id}`
          : '',
    );
    setYieldStr(
      repeatCandidate.planned_yield_c_ha != null
        ? String(repeatCandidate.planned_yield_c_ha)
        : '',
    );
    toast.info(
      `Подставлена культура прошлого сезона: ${repeatCandidate.crop_name ?? ''} — проверьте и посейте`,
    );
  }

  async function handleSave() {
    const yieldVal = yieldStr.trim() === '' ? null : Number(yieldStr);
    if (yieldVal != null && Number.isNaN(yieldVal)) {
      toast.error('План урожайности должен быть числом (ц/га)');
      return;
    }
    try {
      // Культура выбрана из общего справочника / приехала из старого посева и
      // в номенклатуре хозяйства её нет → заводим ОДИН раз при сохранении
      // (решение founder 28.07). Пишем при этом обе связи: канон и номенклатуру.
      let lciId = selectedCrop?.local_catalog_item_id ?? null;
      if (kind === 'crop' && !lciId && cropId) {
        lciId = (await adopt.mutateAsync({ crop_id: cropId })).local_catalog_item_id;
      }

      if (s) {
        // PATCH существующего посева.
        if (kind === 'crop') {
          await patch.mutateAsync({
            sowingId: s.sowing_id,
            body: {
              crop_id: cropId || null,
              local_catalog_item_id: lciId,
              planned_yield_c_ha: yieldVal,
              // [] = очистить назначения, [id…] = заменить набор.
              growing_goal_ids: goalIds,
            },
          });
        } else if (kind === 'fallow') {
          await patch.mutateAsync({
            sowingId: s.sowing_id,
            body: {
              fallow_kind: fallowKind,
              siderat_crop_id: SIDERAT_FALLOW_KINDS.has(fallowKind)
                ? sideratId || null
                : null,
            },
          });
        }
      } else {
        // POST нового посева.
        if (kind === 'crop') {
          if (!cropId) {
            toast.error('Выберите культуру');
            return;
          }
          await create.mutateAsync({
            season_id: seasonId,
            occupancy_type: 'crop',
            subzone_id: leaf.subzone_id,
            crop_id: cropId,
            local_catalog_item_id: lciId,
            planned_yield_c_ha: yieldVal,
            growing_goal_ids: goalIds,
          });
        } else if (kind === 'fallow') {
          await create.mutateAsync({
            season_id: seasonId,
            occupancy_type: 'fallow',
            subzone_id: leaf.subzone_id,
            fallow_kind: fallowKind,
            siderat_crop_id: SIDERAT_FALLOW_KINDS.has(fallowKind)
              ? sideratId || null
              : null,
          });
        }
      }
      toast.success('Посев сохранён');
    } catch (err) {
      handleError(err);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Что сеем на <span className="font-medium text-foreground">{leaf.name}</span>
      </p>

      {/* Плитки выбора: культура / пар / пусто */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {TILES.map((t) => {
          const Icon = t.icon;
          const on = kind === t.kind;
          return (
            <button
              key={t.kind}
              type="button"
              disabled={!canWrite}
              onClick={() => setKind(t.kind)}
              aria-pressed={on}
              className={cn(
                'flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition-colors',
                on
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/60',
                !canWrite && 'cursor-not-allowed opacity-60',
              )}
            >
              <Icon className={cn('h-5 w-5', on ? 'text-primary' : 'text-muted-foreground')} />
              <span className="text-sm font-medium">{t.title}</span>
              <span className="text-xs text-muted-foreground">{t.hint}</span>
            </button>
          );
        })}
      </div>

      {kind === 'crop' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Культура</Label>
            <Select value={cropKey} onValueChange={setCropKey} disabled={!canWrite}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите культуру хозяйства…" />
              </SelectTrigger>
              <SelectContent>
                {pickerItems.map((c) => (
                  <SelectItem key={cropPickerKey(c)} value={cropPickerKey(c)}>
                    {c.name}
                    {/* Термин хозяйства, ещё не сопоставленный с общим
                        справочником: сеять им можно, разберут позже. */}
                    {(c.match_status === 'queued' ||
                      c.match_status === 'suggested') && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · не сопоставлено
                      </span>
                    )}
                    {!c.in_nomenclature && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · нет в справочнике хозяйства
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {picker.isLoading
                ? 'Загружаем культуры хозяйства…'
                : pickerItems.length === 0
                  ? 'У хозяйства пока нет своих культур — добавьте из общего справочника.'
                  : 'Культуры вашего хозяйства. Нужной нет — добавьте из общего справочника.'}
            </p>
          </div>

          {canWrite && addableCrops.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Добавить из общего справочника
              </Label>
              <Select
                value=""
                onValueChange={async (canonId) => {
                  try {
                    const added = await adopt.mutateAsync({ crop_id: canonId });
                    setCropKey(cropPickerKey(added));
                    toast.success(`${added.name} добавлена в культуры хозяйства`);
                  } catch (err) {
                    handleError(err);
                  }
                }}
                disabled={adopt.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Найти культуру в общем справочнике…" />
                </SelectTrigger>
                <SelectContent>
                  {addableCrops.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>
              Назначение посева{' '}
              <span className="text-muted-foreground">(необязательно)</span>
            </Label>
            <MultiSelectChips
              options={goalOptions.map((g) => ({ value: g.id, label: g.name }))}
              selected={goalIds}
              onChange={setGoalIds}
              placeholder="Например: на зелёный корм"
              emptyHint={
                goalsRef.isLoading
                  ? 'Загружаем назначения…'
                  : 'Назначения не найдены'
              }
              disabled={!canWrite}
            />
            <p className="text-xs text-muted-foreground">
              Зачем растим: на зерно, на семена, на зелёный корм. Одна культура —
              разные назначения.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>План урожайности, ц/га</Label>
              <Input
                inputMode="decimal"
                value={yieldStr}
                onChange={(e) => setYieldStr(e.target.value)}
                placeholder="напр. 55"
                disabled={!canWrite}
              />
            </div>
            <div className="space-y-2">
              <Label>Прогресс</Label>
              <div className="pt-2">
                <OccupancyProgress
                  sowing={s}
                  fieldId={fieldId}
                  canWrite={canWrite}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {kind === 'fallow' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>{isRiceSystem ? 'Вид пара или АМП' : 'Вид пара'}</Label>
            {/* Справочник грузится — скелетон вместо пустоты; не загрузился —
                форму не блокируем, пар сохранится с уже выбранным видом. */}
            {fieldStates.isLoading ? (
              <Skeleton className="h-8 w-72" />
            ) : stateOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Справочник видов пара сейчас недоступен — сохраним пар как есть.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {stateOptions.map((f) => (
                  <button
                    key={f.code}
                    type="button"
                    disabled={!canWrite}
                    onClick={() => setFallowKind(f.code)}
                    aria-pressed={fallowKind === f.code}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      fallowKind === f.code
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/60',
                      !canWrite && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            )}
            {isRiceSystem && (
              <p className="text-xs text-muted-foreground">
                АМП — агромелиоративное поле: сезон без культуры, только на
                рисовой системе.
              </p>
            )}
          </div>
          {showSiderat && (
            <div className="space-y-2">
              <Label>
                Культура в пару{' '}
                <span className="text-muted-foreground">(сидерат / ранняя)</span>
              </Label>
              <Select
                value={sideratId}
                onValueChange={setSideratId}
                disabled={!canWrite}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Например: горчица белая" />
                </SelectTrigger>
                <SelectContent>
                  {crops.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {kind === 'none' && (
        <p className="rounded-lg bg-muted px-3 py-3 text-sm text-muted-foreground">
          Посев на этот сезон не запланирован. Выберите «Посеять культуру» или
          «Оставить под пар», когда решение будет принято.
        </p>
      )}

      {canWrite && (kind !== 'none' || (s && canRemove)) && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {kind !== 'none' && (
            <Button
              onClick={handleSave}
              disabled={saving || Boolean(blockedReason)}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {s ? 'Сохранить' : sowButtonLabel(leaf.subzone_id)}
            </Button>
          )}
          {/* Снять посев — выход из «поле занято целиком, а сеять надо по
              зонам» и просто «передумали сеять» (AK-449). */}
          {s && canRemove && (
            <Button variant="outline" onClick={handleRemove} disabled={saving}>
              {remove.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Снять посев
            </Button>
          )}
          {repeatCandidate && (
            <Button
              variant="outline"
              onClick={repeatLastSeason}
              disabled={saving}
              title={`Прошлый сезон: ${repeatCandidate.crop_name ?? ''}`}
            >
              <Undo2 className="mr-2 h-4 w-4" />
              Повторить прошлый сезон
            </Button>
          )}
        </div>
      )}

      {canWrite && blockedReason && kind !== 'none' && (
        <p className="text-xs text-muted-foreground">
          Посеять этот участок сейчас нельзя — сначала снимите посев, который
          мешает (пояснение выше).
        </p>
      )}

      {canWrite && s && !canRemove && (
        <p className="text-xs text-muted-foreground">
          Снять посев может сотрудник с правом удалять посевы — у вас его нет.
        </p>
      )}
    </div>
  );
}
